import React, { useEffect, useState } from "react";
import { sendClientSideSplitTrackEvent } from "@leafly-com/split-next";
import isEmpty from "lodash/isEmpty";
import times from "lodash/times";
import Link from "next/link";

import getStrainReviews from "api/requests/getStrainReviews";
import { useStrainPageContext } from "context/StrainPageContext";
import { StrainReview } from "custom-types/Reviews";
import { Strain } from "custom-types/Strain";
import { numberCommaSeparated } from "utils/numberCommaSeparated";
import { getSplitKey } from "utils/split/getSplitKey";
import { Action, Category, trackEvent } from "utils/trackEvent";

import { ReviewCardPlaceholder } from "components/botanic/ReviewCard";
import StrainReviewCardContainer from "components/StrainReviewCardContainer";
import TrackImpression from "components/TrackImpression";

import Section from "./Section";
import StrainReviewsNoResults from "./StrainReviewsNoResults";

const MAX_REVIEWS_TO_DISPLAY = 3;

const StrainReviews = (): JSX.Element => {
  const { strain } = useStrainPageContext();
  const [reviews, setReviews] = useState<StrainReview[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const { name, slug, reviewCount, rating } = strain;

  const renderReviewSchema = () => {
    if (reviewCount > 0) {
      return (
        <div
          itemProp="aggregateRating"
          itemScope
          itemType="http://schema.org/AggregateRating"
        >
          <meta itemProp="reviewCount" content={`${reviewCount}`} />
          <meta itemProp="bestRating" content={"5"} />
          <meta itemProp="ratingValue" content={`${rating}`} />
        </div>
      );
    }
  };

  useEffect(() => {
    const fetchReviews = async () => {
      const { data } = await getStrainReviews(slug, MAX_REVIEWS_TO_DISPLAY);

      setReviews(data);
      setIsLoading(false);
    };

    if (slug) {
      fetchReviews();
    }
  }, [slug]);

  if (isEmpty(strain)) {
    return <></>;
  }

  return (
    <>
      {renderReviewSchema()}
      <TrackImpression
        label="strain page reviews carousel view"
        category="Carousel"
      >
        <Section
          id="strain-reviews-section"
          testid="strain-reviews-section"
          className="container"
          title={`${name} strain reviews`}
          tidbit={numberCommaSeparated(reviewCount, "No Reviews")}
        >
          <TrackImpression category="Reviews" label="strain write a review CTA">
            <Link
              prefetch={false}
              href={`/strains/${slug}/reviews/write-review`}
              className="button text-center font-bold rounded-full p-md text-default border-default border-2 bg-white"
              style={{ fontSize: "16px" }}
              onClick={() => {
                trackEvent(
                  Category.reviews,
                  Action.click,
                  "strain write a review CTA"
                );
                sendClientSideSplitTrackEvent(
                  "webStrainExplorer_strainWriteReviewClick_frontend",
                  getSplitKey()
                );
              }}
            >
              write a review
            </Link>
          </TrackImpression>
          <div className="py-lg">
            {!isLoading ? (
              reviews.length > 0 ? (
                <>
                  {reviews.map((review) => (
                    <StrainReviewCardContainer
                      review={{ ...review, strain: strain as Strain }}
                      key={`review-card-${review.id}`}
                      truncate
                    />
                  ))}
                </>
              ) : (
                <StrainReviewsNoResults strainSlug={slug} />
              )
            ) : (
              times(Math.min(MAX_REVIEWS_TO_DISPLAY, reviewCount), (i) => (
                <ReviewCardPlaceholder
                  className="elevation-low rounded mb-lg"
                  key={`review-card-placeholder-${i}`}
                />
              ))
            )}
          </div>
          {reviews.length > 0 && (
            <Link
              prefetch={false}
              href={`/strains/${slug}/reviews`}
              className="pt-lg font-sm font-bold text-green underline"
            >
              Read all reviews
            </Link>
          )}
        </Section>
      </TrackImpression>
    </>
  );
};

export default StrainReviews;
